<script setup lang="ts">
import { errorToast, successToast } from "@/utils/custom-toast";
import { defineEmits, defineProps, computed, ref } from "vue";
import client from "@/utils/axios";
import BonusReportForm from "./BonusReportForm.vue";
import BonusReportReview from "./BonusReportReview.vue";
import BonusReportSuccess from "./BonusReportSuccess.vue";
import { useTranslate } from "@/composables/useTranslate";
import { WorkerBonus } from "@/types";

type Props = {
  isReportDialogOpen: boolean;
  bonusDate: any;
  isEdit: boolean;
  month: string | null;
  year: number | null;
};

const props = defineProps<Props>();
const currentDialogStage = ref(1);

type Emits = {
  (event: "onClose"): void;
};

const emits = defineEmits<Emits>();
const isModalOpen = computed(() => props.isReportDialogOpen);
const isModalEdit = computed(() => props.isEdit);

const selectedUrl = ref();
const { t } = useTranslate();
const isLoading = ref(false);

// GET MONTHS
let months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item: any) => {
  return {
    id: item,
    name: t(`months.${item}`),
  };
});

const workersBonuses = ref<Array<WorkerBonus>>([]);

const currentMonthIndex = new Date().getMonth();

const selectedMonth = ref(
  props.month
    ? months.find((m) => m.id === props.month) || months[currentMonthIndex]
    : months[currentMonthIndex]
);
const areBonusesValid = computed(() => {
  if (workersBonuses.value) {
    let isValid = true;
    workersBonuses.value.forEach((workerBonus: WorkerBonus) => {
      if (
        !workerBonus.bonusPayoutTotal.valid ||
        !workerBonus.commercialTotal.valid ||
        !workerBonus.scaffoldingTotal.valid ||
        !workerBonus.bonusTotal.valid
      ) {
        isValid = false;
      }
    });
    return isValid;
  }
  return false;
});
// GET YEARS
const currentYear = new Date().getFullYear();
const rawYears = Array.from({ length: 3 }, (_, i) => currentYear + i);

const years = rawYears.map((year) => ({ id: year, name: year.toString() }));

const selectedYear = ref(
  props.year
    ? years.find((y) => y.id === props.year) || years[0]
    : years.find((y) => y.id === currentYear) || years[0]
);

const handleClose = () => {
  currentDialogStage.value = 1;
  selectedYear.value = years.find((y) => y.id === currentYear) || years[0];
  selectedMonth.value = months[currentMonthIndex];
  selectedUrl.value = "";
  emits("onClose");
};

const handleSuccessClose = () => {
  if (props.isEdit) {
    successToast(
      `Successfully updated bonus report for ${props.bonusDate.month}/${props.bonusDate.year}`
    );
  } else {
    successToast(
      `Successfully added bonus report for ${selectedMonth.value.name}/${selectedYear.value.name}`
    );
  }
  currentDialogStage.value = 1;
  selectedYear.value = years.find((y) => y.id === currentYear) || years[0];
  selectedMonth.value = months[currentMonthIndex];
  selectedUrl.value = "";
  emits("onClose");
};

const increaseDialogStage = () => {
  currentDialogStage.value = currentDialogStage.value + 1;
};

const decreaseDialogStage = () => {
  currentDialogStage.value = currentDialogStage.value - 1;
};

const submitReport = async () => {
  try {
    await client.post("/installers/monthly-bonus/report", {
      month: selectedMonth.value?.id,
      year: selectedYear.value.id,
      sheetUrl: selectedUrl.value,
      workersBonuses: workersBonuses.value,
    });

    increaseDialogStage();
  } catch (error: any) {
    errorToast(error);
    console.log(error);
  }
};

const onUploadBonusSheet = async (month: any, year: any, url: string) => {
  selectedMonth.value = month;
  selectedYear.value = year;
  selectedUrl.value = url;
  isLoading.value = true;
  try {
    const response = await client.post("/installers/monthly-bonus", {
      sheetUrl: selectedUrl.value,
      month: selectedMonth.value?.id,
      year: selectedYear.value?.id,
      isUpdate: props.isEdit,
    });
    isLoading.value = false;
    if (response.status === 200) {
      workersBonuses.value = response.data;

      increaseDialogStage();
    }

    isLoading.value = false;
  } catch (error: any) {
    console.log(error);
    isLoading.value = false;
    if (error.status === 400) {
      errorToast(t(`installers.${error.response?.data}`).value);
    } else {
      errorToast(t(`global.something_went_wrong`).value);
    }
  }
};
</script>
<template>
  <Transition name="nested">
    <div v-if="isModalOpen">
      <BonusReportForm
        v-if="currentDialogStage === 1"
        :selected-month="selectedMonth"
        :selected-year="selectedYear"
        :selected-url="selectedUrl"
        :months="months"
        :years="years"
        :is-report-dialog-open="isModalOpen"
        :is-edit="isEdit"
        :is-loading="isLoading"
        @on-close="handleClose()"
        @increase-dialog-stage="increaseDialogStage()"
        @on-submit-bonus="onUploadBonusSheet"
      >
        <template #custom-header>
          <div v-if="!isModalEdit" class="flex items-center">
            <img
              class="w-[18px] h-[18px]"
              src="@/assets/edit-plus-active.svg"
              alt=""
            />
            <p class="ml-[8px] text-[20px] font-semibold text-[#393840]">
              {{ t("installers.new_bonus_report") }}
            </p>
          </div>

          <div v-else class="flex items-center">
            <img
              class="w-[18px] h-[18px]"
              src="@/assets/pencil-gray.svg"
              alt=""
            />
            <p class="ml-[8px] text-[20px] font-semibold text-[#393840]">
              {{ t("global.edit") }}
              {{ selectedMonth.name + "/" + selectedYear.name }}
            </p>
          </div>
        </template>
      </BonusReportForm>
      <BonusReportReview
        v-if="currentDialogStage === 2"
        :is-report-dialog-open="isModalOpen"
        :workers-bonuses="workersBonuses"
        :selected-month="selectedMonth"
        :selected-year="selectedYear"
        :are-bonuses-valid="areBonusesValid"
        @on-close="handleClose()"
        @decrease-dialog-stage="decreaseDialogStage()"
        @submit-report="submitReport"
      >
      </BonusReportReview>
      <BonusReportSuccess
        v-if="currentDialogStage === 3"
        :is-report-dialog-open="isModalOpen"
        :bonus-report-date="selectedMonth.name + ' ' + selectedYear.name"
        @on-close="handleSuccessClose()"
      >
      </BonusReportSuccess>
    </div>
  </Transition>
</template>
